import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Alert, Box, Card, Chip, Divider, Drawer, IconButton, Skeleton, Stack, Typography, useMediaQuery } from "@mui/material";
import { IconBrandWhatsapp, IconChevronRight, IconCopy, IconLink, IconMessageChatbot, IconMessages, IconPencil, IconPhone, IconRefresh, IconTrash, IconX } from "@tabler/icons";
import PersonAvatar from "src/components/shared/PersonAvatar";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import { EMPTY, formatDate, plural, relativeTime } from "src/components/shared/format";
import { casesApi, copyText } from "src/pages/cases/api";
import { STATUS, money } from "src/pages/cases/format";
import { RouterName } from "src/routes/RouterName";
import { ConfirmDialog } from "src/pages/website-content/kit";
import { whatsappLink } from "./whatsapp";

// One customer at a glance (CMS review rania-13, rania-23, nadia-18): who
// they are, what they paid and owe, whether they can sign in, whether they
// are a test account, and their applications (looked up in Cases by their
// email, since the customer list does not carry them), each opening its
// case. The drawer has its own address (/clients?customer=4), to send a
// colleague.

// The customer's own link, for "Copy link".
export const customerLink = (id) => `${window.location.origin}/clients?customer=${encodeURIComponent(id)}`;

// What the test-account switch says, by what the API knows:
//   test_by_name  a zz…@example.com or "ZZ " account: always a test account
//   test_flag     staff switched it (null before database update 025)
export function testAccountWords(customer) {
  if (customer?.test_by_name) return { on: true, locked: true, text: "A test account by its name or email (zz…@example.com or “ZZ ”), whatever this switch says." };
  if (customer?.test_flag == null) return { on: false, locked: true, text: "Marking test accounts needs database update 025. Until then only zz…@example.com and “ZZ ” accounts count as tests." };
  return customer.test_flag
    ? { on: true, locked: false, text: "Their applications leave every count on Home, Cases, Key numbers and Money while “Hide test accounts” is on." }
    : { on: false, locked: false, text: "Mark an account the team uses for testing: its applications then leave the counts." };
}

export const customerName = (c) => [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim() || c?.email || "This customer";

// An email on one line: cut with "…" when it doesn't fit (never broken
// mid-word), the whole address in its tooltip, and a button to copy it.
export function EmailLine({ email, variant = "body2" }) {
  if (!email)
    return (
      <Typography variant={variant} color="text.secondary">
        {EMPTY}
      </Typography>
    );
  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Typography component="span" variant={variant} color="text.secondary" noWrap title={email} sx={{ minWidth: 0 }}>
        {email}
      </Typography>
      <IconButton
        size="small"
        aria-label={`Copy ${email}`}
        onClick={async (e) => {
          e.stopPropagation();
          if (await copyText(email)) toast.success("Email copied.");
          else toast.error("The email couldn’t be copied. Select it and copy it by hand.");
        }}
        sx={[iconButtonSx, { flexShrink: 0, color: "text.secondary" }]}
      >
        <IconCopy size={16} aria-hidden />
      </IconButton>
    </Stack>
  );
}

EmailLine.propTypes = { email: PropTypes.string, variant: PropTypes.string };

// The first ten, then "Show all" (nadia-09: fifty took 15 s to read).
const FIRST = 10;

// Their applications, found by their account (client_id): exact and fast.
// An older API that doesn't filter by account: by their email, as before.
function Applications({ customerId, email, currentCaseId }) {
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true, failed: false, rows: [] });
  const [all, setAll] = useState(false);

  const load = useCallback(() => {
    if (!customerId && !email) {
      setState({ loading: false, failed: false, rows: [] });
      return;
    }
    setState((s) => ({ ...s, loading: true, failed: false }));
    const byEmail = () =>
      casesApi.list({ view: "all", search: email, page: 0, rowsPerPage: 50 }, dispatch).then((res) => {
        const rows = res?.data?.cases || [];
        // Search matches parts of addresses: keep this customer's only.
        return rows.filter((c) => String(c?.customer?.email || "").toLowerCase() === String(email).toLowerCase());
      });
    const byAccount = customerId
      ? casesApi.list({ view: "all", client_id: Number(customerId), page: 0, rowsPerPage: 100 }, dispatch).then((res) =>
          res?.data?.applied?.client_id != null ? res.data.cases || [] : email ? byEmail() : [],
        )
      : byEmail();
    byAccount.then((rows) => setState({ loading: false, failed: false, rows })).catch(() => setState({ loading: false, failed: true, rows: [] }));
  }, [customerId, email, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading)
    return (
      <Stack spacing={1} aria-busy="true" aria-label="Loading applications">
        {[0, 1].map((i) => (
          <Skeleton key={i} variant="rounded" height={64} />
        ))}
      </Stack>
    );
  if (state.failed)
    return (
      <Alert
        severity="error"
        sx={{ alignItems: "center" }}
        action={
          <ActionButton onClick={load} startIcon={<IconRefresh size={16} />}>
            Try again
          </ActionButton>
        }
      >
        Their applications did not load.
      </Alert>
    );
  if (!state.rows.length)
    return (
      <Typography color="text.secondary" sx={{ py: 1 }}>
        No applications yet.
      </Typography>
    );

  const latest = state.rows.map((r) => r.submitted_at).filter(Boolean).sort().pop();
  const shown = all ? state.rows : state.rows.slice(0, FIRST);
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {plural(state.rows.length, "application")}
        {latest ? ` · last one ${relativeTime(latest)}` : ""}
      </Typography>
      <Stack spacing={1} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {shown.map((c) => {
          const status = STATUS[c.status] ?? { label: c.status, color: "default" };
          const here = currentCaseId != null && Number(c.id) === Number(currentCaseId);
          return (
            <Card
              key={c.id}
              component="li"
              variant="outlined"
              sx={{ p: 0, ...(here ? { borderColor: "primary.main", bgcolor: "primary.light" } : {}), "&:hover": { borderColor: "primary.main" }, "&:focus-within": { borderColor: "primary.main" } }}
            >
              <Box
                component={RouterLink}
                to={`/cases/${c.id}`}
                aria-current={here ? "page" : undefined}
                aria-label={`Case #${c.id}: ${c.destination || "visa"}, ${status.label}${here ? " (this case)" : ""}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  color: "text.primary",
                  borderRadius: "20px",
                  "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: -2 },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} noWrap>
                    <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700, mr: 1 }}>
                      #{c.id}
                    </Box>
                    {c.destination || EMPTY}
                    {here && (
                      <Box component="span" sx={{ ml: 1, fontSize: "0.75rem", fontWeight: 700, color: "primary.main" }}>
                        This case
                      </Box>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {c.product || EMPTY} · {c.submitted_at ? formatDate(c.submitted_at) : "not submitted"}
                  </Typography>
                  <Stack direction="row" spacing={0.5} mt={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={status.color} label={status.label} />
                    {c.status !== "incomplete" && (
                      <Chip size="small" variant="outlined" color={c.is_paid ? "success" : "warning"} label={c.is_paid ? "Paid" : "Not paid"} />
                    )}
                    <Typography variant="body2" sx={{ ml: "auto", fontFamily: "monospace" }}>
                      {money(c.total)}
                    </Typography>
                  </Stack>
                </Box>
                <IconChevronRight size={18} aria-hidden />
              </Box>
            </Card>
          );
        })}
      </Stack>
      {state.rows.length > shown.length && (
        <ActionButton variant="text" onClick={() => setAll(true)} sx={{ mt: 0.5, ml: -1.5 }}>
          Show all {state.rows.length}
        </ActionButton>
      )}
    </>
  );
}

Applications.propTypes = { customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), email: PropTypes.string, currentCaseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };

// Their phone, as typed on their latest application, with "Answer on
// WhatsApp": a link that opens WhatsApp with the number and the case number
// typed in; Safrati sends nothing (round 2, nadia-09, karim-15).
export function PhoneLine({ phone, caseId }) {
  if (!phone) return null;
  const link = whatsappLink(phone, caseId);
  return (
    <Stack direction="row" alignItems="center" columnGap={1} rowGap={0.5} flexWrap="wrap">
      <Typography variant="body2" color="text.secondary" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <IconPhone size={16} aria-hidden />
        <Box component="a" href={`tel:${String(phone).replace(/[^\d+]/g, "")}`} sx={{ color: "inherit" }}>
          {phone}
        </Box>
      </Typography>
      {link ? (
        <ActionButton
          variant="text"
          component="a"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<IconBrandWhatsapp size={16} />}
          aria-label={`Answer on WhatsApp, ${phone}${caseId ? `, about case #${caseId}` : ""} (opens WhatsApp; nothing is sent until you send it)`}
          sx={{ ml: -1 }}
        >
          Answer on WhatsApp
        </ActionButton>
      ) : (
        <Typography variant="caption" color="text.secondary">
          WhatsApp needs the number with its country code.
        </Typography>
      )}
    </Stack>
  );
}

PhoneLine.propTypes = { phone: PropTypes.string, caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };

// Account settings as words with "Change…" that asks first (round 2,
// nadia-10, karim-21): no switch a thumb can brush, and away from the top.
function AccountRow({ title, text, detail, changeLabel, disabled, onChange }) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ py: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
        {detail}
      </Box>
      {onChange && (
        <ActionButton variant="outlined" size="small" disabled={disabled} onClick={onChange} aria-label={changeLabel} sx={{ flexShrink: 0 }}>
          Change…
        </ActionButton>
      )}
    </Stack>
  );
}

AccountRow.propTypes = { title: PropTypes.string, text: PropTypes.node, detail: PropTypes.node, changeLabel: PropTypes.string, disabled: PropTypes.bool, onChange: PropTypes.func };

const cardSx = { p: 2, flexShrink: 0 };

const CustomerPanel = ({ customer, onClose, onEdit, onDelete, onToggle, onTest, canEdit, canDelete, canChats, canSupport, busyToggle, busyTest, currentCaseId }) => {
  const phone = useMediaQuery((t) => t.breakpoints.down("sm"));
  // "Change…" on the test account asks first: { kind: "test" } while open.
  const [asking, setAsking] = useState(null);
  const open = !!customer;
  const name = customerName(customer);
  const active = customer?.is_active == 1 || customer?.is_active === true; // eslint-disable-line eqeqeq
  const test = testAccountWords(customer);
  const hasMoney = customer && customer.paid_total !== undefined;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: phone ? "100%" : 440, borderRadius: phone ? 0 : "20px 0 0 20px" } }}
      aria-labelledby="customer-panel-title"
    >
      {customer && (
        // flexShrink 0: in the drawer's column the panel and its cards keep
        // their height instead of being squeezed and cut (rania-23).
        <Box sx={{ p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", gap: 2.5, minHeight: "100%", flexShrink: 0 }} role="dialog" aria-labelledby="customer-panel-title">
          <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ flexShrink: 0 }}>
            <PersonAvatar name={name} src={customer.image} size={56} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id="customer-panel-title" variant="h4" component="h2" sx={{ overflowWrap: "anywhere" }}>
                {name}
              </Typography>
              <EmailLine email={customer.email} variant="body1" />
              <PhoneLine phone={customer.phone} caseId={currentCaseId} />
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  Customer #{customer.id}
                </Typography>
                {customer.is_test && <Chip size="small" label="Test account" />}
                <ActionButton
                  variant="text"
                  startIcon={<IconLink size={16} />}
                  onClick={async () => {
                    if (await copyText(customerLink(customer.id))) toast.success(`Link to ${name} copied.`);
                    else toast.error("The link couldn’t be copied. Copy it from the address bar.");
                  }}
                  sx={{ ml: -1 }}
                >
                  Copy link
                </ActionButton>
              </Stack>
            </Box>
            <IconButton aria-label="Close" onClick={onClose} sx={iconButtonSx}>
              <IconX size={20} />
            </IconButton>
          </Stack>

          {hasMoney && (
            <Card variant="outlined" sx={cardSx}>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Paid
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "1.25rem" }}>{money(customer.paid_total)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Owes
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "1.25rem", color: customer.owed_total ? "warning.dark" : "text.primary" }}>
                    {money(customer.owed_total)}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Each application’s total, as in Cases. Owed: submitted and not paid; drafts and cancelled left out.
              </Typography>
            </Card>
          )}

          <Box sx={{ flexShrink: 0 }}>
            <Typography component="h3" fontWeight={700} sx={{ mb: 1 }}>
              Applications
            </Typography>
            <Applications customerId={customer.id} email={customer.email} currentCaseId={currentCaseId} />
          </Box>

          {/* The account's settings, below what the panel is opened for. */}
          <Card variant="outlined" sx={cardSx} component="section" aria-labelledby="customer-account-title">
            <Typography id="customer-account-title" component="h3" fontWeight={700}>
              Account
            </Typography>
            <AccountRow
              title="Signing in"
              text={active ? "They can sign in to see their applications." : "Switched off: they cannot sign in."}
              changeLabel={`Change whether ${name} can sign in`}
              disabled={!canEdit || busyToggle}
              // Stopping asks first (the Customers page's own question); letting them back in is instant, with a toast.
              onChange={onToggle && canEdit ? () => onToggle(customer, active ? 0 : 1) : undefined}
            />
            {onTest && (
              <AccountRow
                title="Test account"
                text={test.on ? `Yes. ${test.text}` : `No. ${test.text}`}
                detail={
                  customer.test_flag && customer.test_marked_at ? (
                    <Typography variant="caption" color="text.secondary">
                      Marked {customer.test_marked_by_name ? `by ${customer.test_marked_by_name} ` : ""}on {formatDate(customer.test_marked_at)}.
                    </Typography>
                  ) : null
                }
                changeLabel={`Change whether ${name} is a test account`}
                disabled={!canEdit || busyTest}
                onChange={canEdit && !test.locked ? () => setAsking({ kind: "test" }) : undefined}
              />
            )}
          </Card>
          {onTest && (
            <ConfirmDialog
              open={asking?.kind === "test"}
              title={test.on ? `${name} is not a test account?` : `Mark ${name} as a test account?`}
              confirmLabel={test.on ? "Not a test account" : "Mark as a test account"}
              cancelLabel="Keep it as it is"
              tone="primary"
              onClose={() => setAsking(null)}
              onConfirm={() => onTest(customer, !test.on)}
            >
              {test.on
                ? "Their applications count again on Home, Cases, Key numbers and Money. You can switch it back here."
                : "Their applications leave the counts on Home, Cases, Key numbers and Money while “Hide test accounts” is on. You can switch it back here."}
            </ConfirmDialog>
          )}

          <Divider />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: "auto", flexShrink: 0 }}>
            {canEdit && (
              <ActionButton variant="outlined" startIcon={<IconPencil size={16} />} onClick={() => onEdit(customer)}>
                Edit details
              </ActionButton>
            )}
            {canSupport && (
              <ActionButton variant="text" component={RouterLink} to={RouterName.support} startIcon={<IconMessages size={16} />}>
                Support chats
              </ActionButton>
            )}
            {canChats && (
              <ActionButton variant="text" component={RouterLink} to={`/user-ai-chat-sessions/${customer.id}`} startIcon={<IconMessageChatbot size={16} />}>
                Ask Safrati chats
              </ActionButton>
            )}
            {canDelete && (
              <ActionButton variant="text" tone="destructive" startIcon={<IconTrash size={16} />} onClick={() => onDelete(customer)} sx={{ ml: { sm: "auto" } }}>
                Delete
              </ActionButton>
            )}
          </Stack>
        </Box>
      )}
    </Drawer>
  );
};

CustomerPanel.propTypes = {
  customer: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onToggle: PropTypes.func,
  onTest: PropTypes.func,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  canChats: PropTypes.bool,
  canSupport: PropTypes.bool,
  busyToggle: PropTypes.bool,
  busyTest: PropTypes.bool,
  currentCaseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default CustomerPanel;
